import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, FileText, Eye, Save, XCircle, ChevronDown, ChevronUp,
  Shield, Zap, Calendar, Euro, Building, AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * IntentBridge – Beautiful, adaptive notification for AI-detected contracts
 * Automatically adjusts size based on content richness
 */
export function IntentBridge({ active, contractData, document, onAction }) {
  const [expanded, setExpanded] = useState(false);
  const [showDebug, setShowDebug] = useState(false);

  useEffect(() => {
    if (active && contractData) {
      console.log('🎯 IntentBridge activated:', contractData);
    }
  }, [active, contractData]);

  if (!active || !contractData) return null;

  const {
    provider,
    contract_type,
    monthly_cost,
    start_date,
    end_date,
    cancellation_period,
    auto_renewal,
    confidence
  } = contractData;

  // Determine if we have rich content
  const hasRichContent = monthly_cost || start_date || end_date;
  const hasExtractedText = document?.extracted_text || document?.ocr_extracted_text;

  // Get contract type icon
  const getTypeIcon = (type) => {
    switch(type) {
      case 'insurance': return <Shield className="w-5 h-5" />;
      case 'energy': return <Zap className="w-5 h-5" />;
      case 'telecom': return <Building className="w-5 h-5" />;
      default: return <FileText className="w-5 h-5" />;
    }
  };

  // Format contract type for display
  const formatType = (type) => {
    const types = {
      insurance: 'Versicherung',
      energy: 'Energie',
      telecom: 'Telekommunikation',
      maintenance: 'Wartung',
      other: 'Sonstiges'
    };
    return types[type] || type;
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 50, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 50, scale: 0.95 }}
        transition={{ 
          duration: 0.4, 
          ease: [0.16, 1, 0.3, 1] // Custom easing for smooth feel
        }}
        className={`
          fixed bottom-6 right-6 
          ${expanded ? 'w-[480px]' : hasRichContent ? 'w-96' : 'w-80'}
          bg-white/95 dark:bg-gray-900/95 
          backdrop-blur-xl
          rounded-2xl shadow-2xl 
          border border-gray-200/50 dark:border-gray-700/50
          overflow-hidden
          z-50
          transition-all duration-300
        `}
      >
        {/* Gradient Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-700 dark:to-indigo-700 p-4">
          <div className="flex justify-between items-start">
            <div className="flex items-center space-x-3">
              <div className="bg-white/20 backdrop-blur-sm rounded-lg p-2">
                {getTypeIcon(contract_type)}
              </div>
              <div>
                <h4 className="font-bold text-white text-lg">
                  🎯 Vertrag erkannt
                </h4>
                {confidence && (
                  <p className="text-blue-100 text-xs mt-0.5">
                    KI-Sicherheit: {Math.round(confidence * 100)}%
                  </p>
                )}
              </div>
            </div>
            <button
              className="text-white/80 hover:text-white hover:bg-white/20 rounded-lg p-1.5 transition-all"
              onClick={() => onAction('ignore')}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 space-y-3">
          {/* Provider & Type */}
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100 text-lg">
                {provider || 'Unbekannter Anbieter'}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {formatType(contract_type)}
              </p>
            </div>
            {monthly_cost && (
              <div className="text-right">
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {monthly_cost.toFixed(2)} €
                </p>
                <p className="text-xs text-gray-500">pro Monat</p>
              </div>
            )}
          </div>

          {/* Contract Details (if available) */}
          {hasRichContent && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-200 dark:border-gray-700"
            >
              {start_date && (
                <div className="flex items-center space-x-2 text-sm">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500">Beginn</p>
                    <p className="font-medium text-gray-700 dark:text-gray-300">
                      {new Date(start_date).toLocaleDateString('de-DE')}
                    </p>
                  </div>
                </div>
              )}
              {end_date && (
                <div className="flex items-center space-x-2 text-sm">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500">Ende</p>
                    <p className="font-medium text-gray-700 dark:text-gray-300">
                      {new Date(end_date).toLocaleDateString('de-DE')}
                    </p>
                  </div>
                </div>
              )}
              {cancellation_period && (
                <div className="flex items-center space-x-2 text-sm col-span-2">
                  <AlertCircle className="w-4 h-4 text-amber-500" />
                  <p className="text-gray-600 dark:text-gray-400">
                    Kündigungsfrist: <span className="font-medium">{cancellation_period} Tage</span>
                  </p>
                </div>
              )}
            </motion.div>
          )}

          {/* Extracted Text Preview (expandable) */}
          {hasExtractedText && (
            <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setExpanded(!expanded)}
                className="flex items-center justify-between w-full text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors"
              >
                <span className="font-medium">Extrahierter Text</span>
                {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {expanded && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg max-h-40 overflow-y-auto text-xs text-gray-700 dark:text-gray-300 font-mono"
                >
                  {(document.extracted_text || document.ocr_extracted_text).substring(0, 500)}
                  {(document.extracted_text || document.ocr_extracted_text).length > 500 && '...'}
                </motion.div>
              )}
            </div>
          )}

          {/* Debug Info (collapsible) */}
          <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setShowDebug(!showDebug)}
              className="flex items-center justify-between w-full text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
            >
              <span>🔍 Debug-Ansicht</span>
              {showDebug ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
            {showDebug && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-2 p-3 bg-gray-900 dark:bg-black rounded-lg text-xs text-green-400 font-mono max-h-60 overflow-y-auto"
              >
                <pre className="whitespace-pre-wrap">
                  {JSON.stringify(contractData, null, 2)}
                </pre>
              </motion.div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 pt-2">
            <Button
              size="sm"
              variant="outline"
              className="flex-1 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all"
              onClick={() => onAction('view')}
            >
              <Eye className="w-4 h-4 mr-2" />
              Anzeigen
            </Button>

            <Button
              size="sm"
              className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white shadow-lg hover:shadow-xl transition-all"
              onClick={() => onAction('save')}
            >
              <Save className="w-4 h-4 mr-2" />
              Anlegen
            </Button>

            <Button
              size="sm"
              variant="ghost"
              className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
              onClick={() => onAction('ignore')}
            >
              <XCircle className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Subtle bottom glow effect */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 opacity-50"></div>
      </motion.div>
    </AnimatePresence>
  );
}

